import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../../utils/api';
import { OPENAI_SECOND_OPINION_MODEL } from '../../../../../shared/ai-models.js';

const TYPE_LABEL = { sold_evidence: 'sold evidence', estimated_from_new: 'estimated from new' };
// Show a retry button once a pending row is older than this. Set above the worst-case
// completion time (≤1 min cron pickup + the worker's 280s OpenAI timeout ≈ 6 min) so a
// still-running call never flickers to "retry" just before its result lands.
const STALE_PENDING_MS = 8 * 60 * 1000;
const POLL_MS = 4000;

// Age of a pending row in ms; Infinity if requested_at is missing/unparseable (→ treat as stale/retriable).
function pendingAgeMs(d) {
    if (!d?.requested_at) return Infinity;
    const t = Date.parse(String(d.requested_at).replace(' ', 'T') + 'Z');
    return Number.isNaN(t) ? Infinity : Date.now() - t;
}

/**
 * The two rows are two DIFFERENT strings, taken from two different sources:
 *
 *   row 1  the operator's search term — brand + model, editable here
 *   row 2  `display_name`, Prism's full identity for the item
 *
 * They used to be `name` and `name + details`, both split out of the blob's
 * serialised name — so a single-segment name made `details` empty, the fallback
 * fired, and the dialog printed the same title twice. Nothing was wrong with
 * the panel; it was handed one value twice (pricing-worker
 * `docs/specs_20260727_item_naming.md` §1). Row 2 must never be derived from
 * row 1 again.
 *
 * Row 1 is editable and TRAVELS with the request: the operator can fix a bad
 * search term without touching brand/model, which are identity fields — saving
 * those drops the item's price tiers.
 */
export default function SecondOpinionPanel({ dealId, quoteItemId, sp, onSkipNext, searchTerm = '', displayName = '', refreshKey = 0 }) {
    const [data, setData] = useState(null);     // { status, ... }
    const [confirm, setConfirm] = useState(false);
    const [mode, setMode] = useState('verbatim'); // search-mode choice, same semantics as RepriceDialog
    // The operator's term, seeded from the item and editable before asking.
    // Re-seeded whenever the item changes — otherwise item 2's dialog opens
    // holding item 1's term and researches the wrong thing.
    const [term, setTerm] = useState(searchTerm);
    const [busy, setBusy] = useState(false);    // full-page overlay during an active ask
    const [error, setError] = useState(null);
    const pollRef = useRef(null);
    // Generation stamp per deal/item view. clearInterval stops future poll ticks, but a
    // tick already awaiting its HTTP response still lands after a deal switch and would
    // clobber the new item's state with the old item's pending row — drop stale results.
    const genRef = useRef(0);

    const url = `/api/deals/${dealId}/items/${quoteItemId}/second-opinion`;

    const startPolling = useCallback((gen) => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const r = await api.get(url);
                if (genRef.current !== gen) return; // response for a previous deal/item
                setData(r);
                // stop once the row resolves, or once a never-completing pending goes stale (→ offer retry)
                if (r.status !== 'pending' || pendingAgeMs(r) >= STALE_PENDING_MS) {
                    clearInterval(pollRef.current);
                    setBusy(false);
                }
            } catch { /* transient — keep polling */ }
        }, POLL_MS);
    }, [url]);

    const load = useCallback(async (gen) => {
        try {
            const r = await api.get(url);
            if (genRef.current !== gen) return; // response for a previous deal/item
            setData(r);
            // a fresh pending (e.g. after a page reload mid-call) keeps polling so the result lands
            if (r.status === 'pending' && pendingAgeMs(r) < STALE_PENDING_MS) startPolling(gen);
        } catch (e) { if (genRef.current === gen) setError(e.message || 'load failed'); }
    }, [url, startPolling]);

    // Re-run on `refreshKey` too: a "second opinion — all items" batch fires this
    // item's request from the page (not this panel), so the open panel must reload
    // to pick up its fresh `pending` row and start polling — otherwise it keeps
    // showing the Ask button until the operator navigates away and back.
    useEffect(() => {
        const gen = ++genRef.current;
        setData(null); setError(null); setBusy(false);
        load(gen);
        return () => clearInterval(pollRef.current);
    }, [load, refreshKey]);

    useEffect(() => { setTerm(searchTerm); }, [searchTerm, url]);

    async function ask() {
        const gen = genRef.current;
        setConfirm(false); setBusy(true); setError(null);
        try {
            // The term travels with the request — the server cannot re-derive an
            // edited one, and re-splitting a rendered name is what it replaces.
            const r = await api.post(url, { searchMode: mode, searchTerm: term.trim() });
            if (genRef.current !== gen) return; // user moved on mid-request
            setData(r);
            startPolling(gen);
        } catch (e) {
            if (genRef.current !== gen) return;
            setBusy(false); setError(e.message || 'request failed');
        }
    }

    // Seed the search-mode from the current term and open the confirm dialog. Shared by
    // the first-ask button and the post-result "re-run" links (one-per-item was dropped,
    // so an ok/no_data result no longer locks the panel — the operator can research an
    // edited title again).
    const openConfirm = () => { setMode(term.trim() ? 'verbatim' : 'auto'); setConfirm(true); };

    const st = data?.status;
    const stalePending = st === 'pending' && pendingAgeMs(data) >= STALE_PENDING_MS;
    const inProgress = !busy && st === 'pending' && !stalePending;
    const canAsk = !busy && (st === undefined || st === 'none' || st === 'error' || stalePending);

    return (
        <div className="qa-so">
            <div className="qa-so-head">🤖 ChatGPT — second opinion
                {st === 'ok' && data.cost_usd != null && <span className="qa-muted qa-so-meta">{data.model || OPENAI_SECOND_OPINION_MODEL} · {String(data.requested_at || '').slice(0, 16)} · {Math.round((data.latency_ms || 0) / 1000)}s · ${data.cost_usd}</span>}
            </div>

            {busy && (
                <div className="qa-overlay">
                    <div className="qa-overlay-box">
                        <div className="qa-spinner" />
                        <div>Asking ChatGPT… (~1–2 min)</div>
                        {onSkipNext && (
                            <button className="btn btn-secondary qa-so-skipnext"
                                onClick={() => { setBusy(false); onSkipNext(); }}>
                                Do the next item in the meantime ▶
                            </button>
                        )}
                        <div className="qa-muted qa-so-skipnote">The research keeps running — the result will be on this item when you come back.</div>
                    </div>
                </div>
            )}

            {inProgress && (
                <div className="qa-so-running"><span className="qa-spinner qa-spinner-sm" /> ChatGPT is still researching this item… (~1–2 min). It keeps running in the background — check back shortly.</div>
            )}

            {!busy && canAsk && (
                <div>
                    <button className="ask-btn" onClick={openConfirm}>🤖 Ask ChatGPT — second opinion</button>
                    {st === 'error' && <div className="qa-error" role="alert">Last attempt failed{data.error ? `: ${data.error}` : ''}.</div>}
                    {stalePending && <div className="qa-error" role="alert">The previous attempt didn't finish in time — try again.</div>}
                    <div className="qa-muted qa-so-note">Web-search pricing via {OPENAI_SECOND_OPINION_MODEL} · ~1–2 min · ~$0.25.</div>
                </div>
            )}

            {!busy && st === 'no_data' && (
                <div>
                    <span className="badge badge-neutral">no data</span> <span className="qa-muted">No second-hand sales or retail price found.</span>
                    <div><button className="qa-so-rerun" onClick={openConfirm}>↻ Re-run second opinion</button></div>
                </div>
            )}

            {!busy && st === 'ok' && (
                <div>
                    <div className="qa-so-headline">
                        <span className="qa-so-price">£{data.price}</span>
                        <span className={`badge ${data.type === 'sold_evidence' ? 'badge-success' : 'badge-warning'}`}>{TYPE_LABEL[data.type] || data.type}</span>
                        {sp != null && <span className="qa-so-vs">our SP: £{sp}</span>}
                    </div>
                    {data.response?.price_basis && (
                        <div className="qa-muted qa-so-basis">
                            {data.type === 'estimated_from_new'
                                ? `from new £${data.new_price}`
                                : `${data.response.price_basis.method} of ${data.response.price_basis.comps_found} comps${data.response.price_basis.low != null ? ` · range £${data.response.price_basis.low}–£${data.response.price_basis.high}` : ''}`}
                        </div>
                    )}
                    {data.response?.sources?.length > 0 && (
                        <div className="qa-so-sources">
                            <div className="qa-so-srchead">Sources ({data.response.sources.length})</div>
                            {data.response.sources.map((s, i) => (
                                <div key={i} className="qa-so-src">
                                    <span className="qa-so-mkt">{s.name}</span>
                                    <span className="qa-muted">{(s.evidence_type || '').replace(/_/g, ' ')}</span>
                                    <span className="qa-so-sp">£{s.observed_price}</span>
                                    <span className="qa-muted">{s.note}</span>
                                    {s.url ? <a href={s.url} target="_blank" rel="noreferrer">↗ open</a> : <span className="qa-muted">no link</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    <div><button className="qa-so-rerun" onClick={openConfirm}>↻ Re-run second opinion</button></div>
                </div>
            )}

            {error && <div className="qa-error" role="alert">{error}</div>}

            {confirm && (
                <div className="modal-overlay" onClick={() => setConfirm(false)}>
                    <div className="modal-content qa-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header"><h3>Ask ChatGPT for a second opinion?</h3><button className="modal-close" onClick={() => setConfirm(false)}>&times;</button></div>
                        <div className="modal-body-scroll">
                            <p>This runs a web-search price lookup with <strong>{OPENAI_SECOND_OPINION_MODEL}</strong>.</p>
                            <label className="qa-reprice-opt">
                                <input type="radio" name="so-search-mode" value="verbatim"
                                    checked={mode === 'verbatim'} onChange={() => setMode('verbatim')} />
                                <span><strong>Search my item name verbatim</strong> — ChatGPT researches exactly:<br />
                                    <input type="text" className="qa-so-term" aria-label="Search term"
                                        placeholder="(no item name)" value={term}
                                        onChange={e => setTerm(e.target.value)}
                                        onFocus={() => setMode('verbatim')} /></span>
                            </label>
                            <label className="qa-reprice-opt">
                                <input type="radio" name="so-search-mode" value="auto"
                                    checked={mode === 'auto'} onChange={() => setMode('auto')} />
                                <span><strong>Automatic</strong> — the item's full identity:<br />
                                    <code>{displayName || term || '(no description)'}</code></span>
                            </label>
                            <p className="qa-muted">The item's condition is appended to either string.</p>
                            <p>⏱ Takes <strong>~1–2 minutes</strong> · 💷 costs <strong>~$0.25</strong> per call.<br />Re-running <strong>replaces</strong> the current result.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setConfirm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={ask}
                                disabled={mode === 'verbatim' && !term.trim()}>Yes, ask ChatGPT</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
